import { useReactiveVar } from '@apollo/client';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { ResponsiveMode } from '@fluentui/react/lib/ResponsiveMode';
import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import {
    DefaultDynamicRouteInput,
    templateInstanceApproverRouteTypeVar,
    templateInstanceDynamicRouteVar,
    templateInstanceFallbackPolicyVar,
    templateInstanceStaticRouteVar,
    templateInstanceStepRoutesVar,
} from '../../../../../../apollo/reactiveVariables/TemplateInstanceVariables';
import {
    currentScopeInCreationVar,
    currentTeamInCreationVar,
    TemplateScope,
} from '../../../../../../apollo/reactiveVariables/TemplateManagementVariables';
import { LogCategory } from '../../../../../../logger';
import { ApprovalPersonaProps } from '../../../../../../models/ApprovalPersonaProps';
import {
    DynamicRouteInput,
    DynamicRouteType,
    FallbackPolicyType,
    RouteTypes,
} from '../../../../../../service/gen/templates';
import { getTooltipHostStyles } from '../../../../../../styles/custom/TooltipHostStyles';
import { getAppHost } from '../../../../../../utilities/TeamsUtils';
import {
    GetDynamicRouteInputItemsByType,
    IPersonaProps2PeopleResults,
    MixedResults2IPersonaProps,
    MixedRouteType,
    TemplateInstanceValidator,
    UI_DynamicRouteInput,
    UI_DynamicRouteItemInput,
    UI_FallbackPolicyInput,
    UI_StaticRouteInput,
} from '../../../../../../utilities/TemplateUtils';
import { stringFormat } from '../../../../../../utilities/Utils';
import { TemplatePeoplePicker } from '../../../../TemplatePeoplePicker';
import { DataSourceType } from '../../../../TemplatePeoplePicker/TemplatePeoplePicker.container';
import { getDropdownStyles } from '../WorkflowSettings.styles';
import { FallbackPolicyComponent } from './FallbackPolicy/FallbackPolicy';
import type { UI_StepRouteSettingInput } from './TemplateSequentialApproverSettings';

export interface TemplateInstanceApproverSettingsV2Props {
    approverRouteType: RouteTypes;
    onApproverRouteTypeChange?: (newType: RouteTypes) => void;
    staticRoute?: UI_StaticRouteInput;
    onStaticRouteChange?: (newVal: UI_StaticRouteInput) => void;
    dynamicRoute?: UI_DynamicRouteInput;
    allowDirectManager?: boolean;
    allowTeamTag?: boolean;
    onDynamicRouteChange?: (newVal: DynamicRouteInput) => void;
    fallbackPolicy?: UI_FallbackPolicyInput;
    onFallbackPolicyChange?(newVal: UI_FallbackPolicyInput): void;
    dataSourceType: DataSourceType;
    dataSourceTeamId?: string;
    // for seqquential approver settings
    stepRouteSetting?: UI_StepRouteSettingInput[];
    onStepRouteSettingChange?: (
        callback:
            | UI_StepRouteSettingInput[]
            | ((stepSettings: UI_StepRouteSettingInput[]) => UI_StepRouteSettingInput[])
    ) => void;
    inline?: boolean;
    hideLabel?: boolean;
}

const validateStaticRoute = (staticRoute: UI_StaticRouteInput): boolean => {
    return (
        !TemplateInstanceValidator.isApproversEmpty(staticRoute?.Approvers) &&
        !TemplateInstanceValidator.isApproversExceeding(staticRoute?.Approvers)
    );
};
const validateDynamicRoute = (dynamicRoute: UI_DynamicRouteInput): boolean => {
    // Only support 1 dynamic at 1 time for now.
    if (!dynamicRoute?.Items || dynamicRoute?.Items.length !== 1) return false;
    // Only direct manager dynamic route is supported for now.
    if (dynamicRoute?.Items[0].Type !== DynamicRouteType.DirectManager) return false;
    return true;
};

const validateStaticAndTagDynamicRoute = (
    staticRoute: UI_StaticRouteInput,
    dynamicRoute: UI_DynamicRouteInput
): boolean => {
    const totalApproversNumber = getTotalApproversNumber(staticRoute, dynamicRoute);
    const IsEmpty = totalApproversNumber === 0;
    const maxNumberOfApprovers = getAppHost().getEcsConfig().MicrosoftTeamApprovals.MaxNumberOfApprovers;
    const IsExceed = totalApproversNumber > maxNumberOfApprovers;
    return !IsEmpty && !IsExceed;
};

const validateFallbackPolicy = (fallbackPolicy: UI_FallbackPolicyInput): boolean => {
    if (fallbackPolicy?.Type === FallbackPolicyType.LetUserEnter) {
        return true;
    } else if (fallbackPolicy?.Type === FallbackPolicyType.FallbackApprovers) {
        return (
            !TemplateInstanceValidator.isApproversEmpty(fallbackPolicy?.FallbackApprovers) &&
            !TemplateInstanceValidator.isApproversExceeding(fallbackPolicy?.FallbackApprovers)
        );
    } else {
        return false;
    }
};

const validateV2 = ({
    staticRoute,
    dynamicRoute,
    approverRouteType,
    fallbackPolicy,
}: {
    approverRouteType: RouteTypes;
    staticRoute?: UI_StaticRouteInput;
    dynamicRoute?: UI_DynamicRouteInput;
    fallbackPolicy?: UI_FallbackPolicyInput;
}): boolean => {
    switch (approverRouteType) {
        case RouteTypes.LetUserEnter:
            return true;
        case RouteTypes.StaticRoute:
            return validateStaticRoute(staticRoute);
        case RouteTypes.DynamicRoute:
            return validateDynamicRoute(dynamicRoute) && validateFallbackPolicy(fallbackPolicy);
        case RouteTypes.StaticRoute | RouteTypes.DynamicRoute:
            return validateStaticAndTagDynamicRoute(staticRoute, dynamicRoute);
        default:
            return false;
    }
};

const validateStepSetting = (stepSettings: UI_StepRouteSettingInput[]): boolean => {
    if (!stepSettings?.length) {
        return false;
    }
    for (const stepSetting of stepSettings) {
        if (!validateV2(stepSetting)) {
            return false;
        }
    }
    return !stepSettings.some((stepSetting: UI_StepRouteSettingInput): boolean => !!stepSetting.error);
};

/**
 * @return
 * 1. the props needed to render TemplateInstanceApproverSettingsV2 component
 * 2. validation result of current template instance routes input
 *
 * @description
 * custom hook dedicated for TemplateInstanceApproverSettingsV2 component
 * return the props needed to render TemplateInstanceApproverSettingsV2 component to make the component easier to use
 * and also make the usage code and implementation code more simple and readable.
 *
 * @note
 * 1. This hook coupled with some Reactive Vars.
 * 2. The unit test of this hook go with the ut of TemplateInstanceApproverSettingsV2 componet
 */
export const useTemplateInstanceApproverSettingsV2 = (): TemplateInstanceApproverSettingsV2Props & {
    templateInstanceApproversValid: boolean;
} => {
    /* States of Appprover Route Type setting. */
    const approverRouteType = useReactiveVar(templateInstanceApproverRouteTypeVar);
    const onApproverRouteTypeChange = (newType: RouteTypes): void => {
        templateInstanceDynamicRouteVar(DefaultDynamicRouteInput);
        templateInstanceApproverRouteTypeVar(newType);
    };
    const stepRouteSetting = useReactiveVar(templateInstanceStepRoutesVar);

    /* States of Static Route setting. */
    const staticRoute = useReactiveVar(templateInstanceStaticRouteVar);
    const onStaticRouteChange = (newVal: UI_StaticRouteInput): void => {
        templateInstanceStaticRouteVar(newVal);
    };

    /* States of Dynamic Route setting. */
    const dynamicRoute = useReactiveVar(templateInstanceDynamicRouteVar);
    const onDynamicRouteChange = (newVal?: UI_DynamicRouteInput): void => {
        templateInstanceDynamicRouteVar(newVal ?? DefaultDynamicRouteInput);
    };

    /* States of Fallback Policy setting. */
    const fallbackPolicy = useReactiveVar(templateInstanceFallbackPolicyVar);
    const onFallbackPolicyChange = (newVal: UI_FallbackPolicyInput): void => {
        templateInstanceFallbackPolicyVar(newVal);
    };

    /* States of Template Management state. */
    const currentScope = useReactiveVar(currentScopeInCreationVar);
    const currentTeam = useReactiveVar(currentTeamInCreationVar);
    const dataSourceType =
        currentScope === TemplateScope.Team ? DataSourceType.TeamWidePeople : DataSourceType.TenantWidePeople;

    /* States of Template Approvers validation. */
    const validateTemplateInstanceApprovers = React.useCallback((): boolean => {
        if (approverRouteType === RouteTypes.StepRoute) {
            return validateStepSetting(stepRouteSetting);
        } else {
            return validateV2({ staticRoute, dynamicRoute, approverRouteType, fallbackPolicy });
        }
    }, [approverRouteType, stepRouteSetting, staticRoute, dynamicRoute, fallbackPolicy]);
    const [templateInstanceApproversValid, setTemplateInstanceApproversValid] = React.useState(
        validateTemplateInstanceApprovers
    );

    /* This hook validate template instance approvers on update. */
    React.useEffect((): void => {
        const approversValidateResult = validateTemplateInstanceApprovers;
        setTemplateInstanceApproversValid(approversValidateResult);
    }, [approverRouteType, staticRoute, dynamicRoute, validateTemplateInstanceApprovers]);

    /* If we should provide dynamic route options (direct manager only for now) */
    const directManagerEnabled =
        getAppHost().getEcsConfig().MicrosoftTeamApprovals.OnlineWorkflowSchemaVersion !== 'v1.0';
    const allowDirectManager =
        (currentScope === TemplateScope.TenantAll || currentScope === TemplateScope.TenantSpecificPeople) &&
        directManagerEnabled;
    const teamTagEnabled = getAppHost().getEcsConfig().MicrosoftTeamApprovals.TeamTagEnabled;

    const allowTeamTag = currentScope === TemplateScope.Team && teamTagEnabled;

    return {
        approverRouteType,
        onApproverRouteTypeChange,
        staticRoute,
        onStaticRouteChange,
        dynamicRoute,
        allowDirectManager,
        onDynamicRouteChange,
        fallbackPolicy,
        onFallbackPolicyChange,
        dataSourceType,
        allowTeamTag,
        dataSourceTeamId: currentTeam?.id,
        templateInstanceApproversValid,
        stepRouteSetting,
        onStepRouteSettingChange: (
            callback: (items: UI_StepRouteSettingInput[]) => UI_StepRouteSettingInput[]
        ): void => {
            templateInstanceStepRoutesVar(
                updateDuplicateStepError(Array.isArray(callback) ? callback : callback(templateInstanceStepRoutesVar()))
            );
        },
    };
};

function updateDuplicateStepError(stepRouteSettings: UI_StepRouteSettingInput[]): UI_StepRouteSettingInput[] {
    const approvers: string[] = [];
    const strings = getAppHost().getLocale().strings;
    for (const stepRouteSetting of stepRouteSettings) {
        const { approverRouteType, staticRoute, fallbackPolicy } = stepRouteSetting;
        if (approverRouteType & RouteTypes.StaticRoute) {
            for (const approver of staticRoute.Approvers) {
                if (approvers.includes(approver.UserAadObjectId)) {
                    stepRouteSetting.error = strings.duplicate_step_approvers_error;
                    return stepRouteSettings;
                } else {
                    approvers.push(approver.UserAadObjectId);
                }
            }
        }

        if (approverRouteType & RouteTypes.DynamicRoute) {
            for (const approver of fallbackPolicy.FallbackApprovers) {
                if (approvers.includes(approver.UserAadObjectId)) {
                    stepRouteSetting.error = strings.duplicate_step_approvers_error;
                    return stepRouteSettings;
                } else {
                    approvers.push(approver.UserAadObjectId);
                }
            }
        }

        stepRouteSetting.error = null;
    }
    return stepRouteSettings;
}
function getTotalApproversNumber(staticRoute?: UI_StaticRouteInput, dynamicRoute?: UI_DynamicRouteInput): number {
    const staticRouteItemsCount = staticRoute?.Approvers?.length ?? 0;
    const tagDynamicRouteItemsCount =
        dynamicRoute?.Items?.filter(
            (item: UI_DynamicRouteItemInput): boolean => item.Type === DynamicRouteType.TeamWorkTag
        ).reduce((sum: number, item: UI_DynamicRouteItemInput): number => sum + item.TeamTagMemberCount ?? 0, 0) ?? 0;
    return staticRouteItemsCount + tagDynamicRouteItemsCount;
}

export const TemplateInstanceApproverSettingsV2: React.FC<TemplateInstanceApproverSettingsV2Props> = ({
    approverRouteType,
    onApproverRouteTypeChange,
    staticRoute,
    onStaticRouteChange,
    allowDirectManager,
    fallbackPolicy,
    onFallbackPolicyChange,
    onDynamicRouteChange,
    dataSourceType,
    dataSourceTeamId,
    dynamicRoute,
    inline,
    hideLabel,
}: TemplateInstanceApproverSettingsV2Props): JSX.Element => {
    const loc = getAppHost().getLocale();
    const logger = getAppHost().getLogger();
    const teamTagEnabled = getAppHost().getEcsConfig().MicrosoftTeamApprovals.TeamTagEnabled;
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;
    /* Approvers route type dropdown options */
    const approverRouteTypeOptions = React.useMemo((): IDropdownOption[] => {
        if (teamTagEnabled) {
            const options: IDropdownOption[] = [
                { key: RouteTypes.LetUserEnter, text: loc.strings.let_submitter_enter_option_text },
                {
                    key: RouteTypes.DynamicRoute | RouteTypes.StaticRoute,
                    data: DynamicRouteType.TeamWorkTag,
                    text: loc.strings.specify_approvers_option_text,
                },
            ];
            if (allowDirectManager) {
                const directManagerOption = {
                    key: RouteTypes.DynamicRoute,
                    data: DynamicRouteType.DirectManager,
                    text: loc.strings.direct_manager_as_approver_option_text,
                };
                options.push(directManagerOption);
            }
            return options;
        }

        const options: IDropdownOption[] = [
            { key: RouteTypes.LetUserEnter, text: loc.strings.let_submitter_enter_option_text },
            { key: RouteTypes.StaticRoute, text: loc.strings.specify_approvers_option_text },
        ];
        if (allowDirectManager) {
            const directManagerOption = {
                key: RouteTypes.DynamicRoute,
                data: DynamicRouteType.DirectManager,
                text: loc.strings.direct_manager_as_approver_option_text,
            };
            options.push(directManagerOption);
        }

        return options;
    }, [teamTagEnabled, loc.strings, allowDirectManager]);

    /* Static Route Validation */
    const [staticRouteError, setStaticRouteError] = React.useState<string>(null);
    const validateRoute = (staticRoute: UI_StaticRouteInput, dynamicRoute?: UI_DynamicRouteInput): boolean => {
        if ((approverRouteType & RouteTypes.StaticRoute) !== RouteTypes.StaticRoute) {
            setStaticRouteError(null);
            return true;
        }
        const totalApprovers = getTotalApproversNumber(staticRoute, dynamicRoute);

        const IsEmpty = totalApprovers === 0;
        const maxNumberOfApprovers = getAppHost().getEcsConfig().MicrosoftTeamApprovals.MaxNumberOfApprovers;
        const IsExceed = totalApprovers > maxNumberOfApprovers;
        if (IsExceed) {
            logger.logTraceWarning(LogCategory.TemplateInstanceWizard, 'Maximum number of approvers reached!');
        }
        let error = null;
        if (IsEmpty) {
            error = loc.strings.required;
        } else if (IsExceed) {
            error = loc.strings.maximum_approvers_error;
        }
        setStaticRouteError(error);
    };
    const selectedItems = MixedResults2IPersonaProps(
        staticRoute?.Approvers,
        GetDynamicRouteInputItemsByType(dynamicRoute, DynamicRouteType.TeamWorkTag)
    );
    const onApproverChange = (newApprovers: ApprovalPersonaProps[]): void => {
        const teamTagPersonnas = newApprovers.filter((item: ApprovalPersonaProps): boolean => item.isTeamTag);
        const noramlPersonnas = newApprovers.filter((item: ApprovalPersonaProps): boolean => !item.isTeamTag);
        const totalPersonnasNumber =
            teamTagPersonnas.reduce((s: number, i: ApprovalPersonaProps): number => s + i.teamTagMemberCount, 0) +
            newApprovers.length;
        const newStaticRoute = {
            ...staticRoute,
            Approvers: IPersonaProps2PeopleResults(noramlPersonnas),
            AllMustRespond: totalPersonnasNumber <= 1 ? true : staticRoute.AllMustRespond,
        };
        const newTagDynamicRoute = teamTagPersonnas.length
            ? ({
                  AllMustRespond: totalPersonnasNumber <= 1 ? true : staticRoute.AllMustRespond,
                  Items: teamTagPersonnas.map(
                      (item: ApprovalPersonaProps): UI_DynamicRouteItemInput => ({
                          Type: DynamicRouteType.TeamWorkTag,
                          TeamTagId: item.id,
                          TeamTagDisplayName: item.text,
                          TeamTagMemberCount: item.teamTagMemberCount,
                          TeamId: item.teamId,
                      })
                  ),
              } as UI_DynamicRouteInput)
            : undefined;
        validateRoute(newStaticRoute, newTagDynamicRoute);
        onStaticRouteChange?.(newStaticRoute);
        onDynamicRouteChange?.(newTagDynamicRoute);
    };
    const onRouteApproverBlur = (): void => {
        validateRoute(staticRoute, dynamicRoute);
    };
    const onAllMustRespondChange = (event: any, newVal?: boolean): void => {
        onStaticRouteChange?.({ ...staticRoute, AllMustRespond: newVal });
        onDynamicRouteChange?.({ ...dynamicRoute, AllMustRespond: newVal });
    };

    const totalApproversNumber = getTotalApproversNumber(staticRoute, dynamicRoute);

    React.useEffect((): void => {
        // init check
        validateRoute(staticRoute, dynamicRoute);
    }, []);

    return (
        <>
            <Stack horizontal>
                <Dropdown
                    label={hideLabel ? '' : loc.strings.approver_option_aria_label}
                    styles={getDropdownStyles}
                    responsiveMode={ResponsiveMode.large}
                    options={approverRouteTypeOptions}
                    onChange={(_event: any, option?: IDropdownOption): void =>
                        onApproverRouteTypeChange(option.key as RouteTypes)
                    }
                    selectedKey={approverRouteType}
                    ariaLabel={loc.strings.approver_option_aria_label}
                />
                {approverRouteType === RouteTypes.DynamicRoute && (
                    <TooltipHost
                        styles={getTooltipHostStyles(theme, {
                            root: {
                                marginTop: 'auto',
                                height: '2rem',
                                paddingLeft: '0.5rem',
                                paddingRight: '0.5rem',
                            },
                        })}
                        content={loc.strings.direct_manager_as_approver_option_tooltip}
                        directionalHint={DirectionalHint.rightCenter}>
                        <FontIcon
                            iconName='Info'
                            tabIndex={0}
                            aria-label={loc.strings.direct_manager_as_approver_option_tooltip}
                        />
                    </TooltipHost>
                )}
            </Stack>
            {(approverRouteType === RouteTypes.StaticRoute || approverRouteType === MixedRouteType) && (
                <div
                    className={
                        approverRouteType === RouteTypes.StaticRoute
                            ? 'template-management-static-route'
                            : 'template-management-mixed-route'
                    }>
                    <TemplatePeoplePicker
                        enableTeamTag={teamTagEnabled}
                        onApproversSelected={onApproverChange}
                        error={staticRouteError}
                        currentSelectedItems={selectedItems}
                        hideLabel={inline}
                        label={loc.strings.visibility_scope_column_header}
                        dataSourceType={dataSourceType}
                        dataSourceTeamId={dataSourceTeamId}
                        onBlur={onRouteApproverBlur}
                        required
                    />
                    {!inline && (
                        <Toggle
                            disabled={totalApproversNumber < 2}
                            checked={staticRoute?.AllMustRespond}
                            label={loc.strings.all_must_respond_toggle_label}
                            inlineLabel
                            onChange={onAllMustRespondChange}
                            ariaLabel={stringFormat(loc.strings.all_must_approve_aria_label, {
                                state: staticRoute?.AllMustRespond
                                    ? loc.strings.checked_aria_label
                                    : loc.strings.unchecked_aria_label,
                            })}
                        />
                    )}
                </div>
            )}
            {approverRouteType === RouteTypes.DynamicRoute && (
                <FallbackPolicyComponent
                    label={loc.strings.direct_manager_is_missing_hint}
                    inline={inline}
                    fallbackPolicy={fallbackPolicy}
                    onFallbackPolicyChange={onFallbackPolicyChange}
                />
            )}
        </>
    );
};
